import { createFileRoute } from '@tanstack/react-router';
import { DelayedSkeleton, ErrorState, PageHeader, Stack } from '@leanin/ui';
import { AdminShell } from '@/components/admin/AdminShell';
import { PLATFORM_ADMIN_ITEMS } from '@/components/admin/admin-sidebar-items';
import { BroadcastComposerForm } from '@/components/broadcasts/BroadcastComposerForm';
import {
  audienceStateFromSpec,
  type AudienceSpec,
} from '@/components/broadcasts/BroadcastEditorFields';
import { LoadingState } from '@/components/states/index';
import { useAdminBroadcastAudienceTargets } from '@/lib/queries/admin';
import { parseAdminBroadcastMembersParam } from '@/lib/routes';

// `?members=<uuid>,<uuid>` prefills a hand-picked members audience (the
// /admin/members bulk "Send email" action). Invalid or over-cap values are
// dropped rather than 404ing — the route still renders a blank composer.
type NewBroadcastSearch = {
  readonly members?: string;
};

export const Route = createFileRoute('/_authed/admin/broadcasts/new/')({
  validateSearch: (raw: Record<string, unknown>): NewBroadcastSearch => {
    const ids = parseAdminBroadcastMembersParam(raw.members);
    return ids !== null ? { members: ids.join(',') } : {};
  },
  component: NewBroadcastPage,
});

function NewBroadcastPage() {
  const { members } = Route.useSearch();
  const memberIds =
    members !== undefined ? parseAdminBroadcastMembersParam(members) : null;

  return (
    <AdminShell items={PLATFORM_ADMIN_ITEMS} active="broadcasts">
      <Stack gap="xl">
        <PageHeader
          back={{ href: '/admin/broadcasts', label: 'Admin Panel · Communications' }}
          title="New broadcast"
          subtitle="Email a circle, network, or the whole community. Schedule for later or send now."
        />
        {memberIds === null ? (
          <BroadcastComposerForm audience={{ mode: 'platform' }} />
        ) : (
          <MembersPrefilledComposer memberIds={memberIds} />
        )}
      </Stack>
    </AdminShell>
  );
}

// Hydrates the picker chips (name + email) for the prefilled ids the same way
// the edit form hydrates a stored hand-picked audience, then seeds the
// composer's audience with them.
function MembersPrefilledComposer({
  memberIds,
}: {
  readonly memberIds: readonly string[];
}) {
  const spec: AudienceSpec = { kind: 'members', userIds: [...memberIds] };
  const targets = useAdminBroadcastAudienceTargets(spec);

  if (targets.isError) {
    return (
      <ErrorState
        title="We couldn't load the selected members."
        body="Go back to Members and try again."
      />
    );
  }
  if (targets.data === undefined) {
    return (
      <DelayedSkeleton
        isLoading
        fallback={<LoadingState rows={4} label="Loading members" />}
      >
        {null}
      </DelayedSkeleton>
    );
  }
  return (
    <BroadcastComposerForm
      audience={{ mode: 'platform' }}
      initial={{
        kind: 'audience',
        audience: audienceStateFromSpec(spec, targets.data),
      }}
    />
  );
}
